import React from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Sparkles, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { mergeAndSortChangelog } from "../lib/changelog";

/**
 * WhatsNewModal — clean, timeline-style list of user-facing release notes.
 *
 * Backing data comes from `GET /api/changelog` so we can ship a fresh
 * entry without a frontend redeploy. The bundled JSON is retained as a
 * belt-and-braces fallback for the very first paint of a session that
 * happens to fire before the API resolves (or if the backend is briefly
 * unreachable — walkers never see an empty modal).
 */
export default function WhatsNewModal({ open, onClose }) {
  const cq = useQuery({
    queryKey: ["changelog"],
    queryFn: async () => (await api.get("/changelog")).data.items || [],
    staleTime: 1000 * 60 * 5,
    enabled: open,
  });

  if (!open) return null;

  // Merged + date-sorted list — same helper AppShell uses to derive the
  // acknowledgment version, so the "Latest" badge on `entries[0]` always
  // matches the version written to localStorage on dismiss.
  const entries = mergeAndSortChangelog(cq.data);

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="whats-new-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-brand-surface rounded-3xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col animate-fade-up"
      >
        <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-brand-border/60">
          <div>
            <div className="td-section-label">Updates</div>
            <h3 className="font-heading text-2xl text-brand-text mt-0.5 flex items-center gap-2">
              <Sparkles size={18} className="text-brand-accent" /> What&apos;s new
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-brand-subtle p-1 rounded-full hover:bg-brand-sand"
            aria-label="Close"
            data-testid="whats-new-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {cq.isLoading && (
            <div className="flex items-center justify-center gap-2 text-sm text-brand-subtle py-8" data-testid="whats-new-loading">
              <Loader2 size={16} className="animate-spin" /> Fetching latest updates…
            </div>
          )}
          {!cq.isLoading && (
            <ol className="relative border-l-2 border-brand-primary/30 pl-6 space-y-6" data-testid="whats-new-list">
              {entries.map((entry, idx) => (
                <li key={entry.version} data-testid={`whats-new-entry-${entry.version}`} className="relative">
                  <span
                    className={`absolute -left-[33px] top-1 w-4 h-4 rounded-full border-2 ${
                      idx === 0
                        ? "bg-brand-accent border-brand-accent"
                        : "bg-brand-sand border-brand-primary/50"
                    }`}
                  />
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <h4 className="font-heading text-lg text-brand-text">{entry.title}</h4>
                    {idx === 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-brand-accent/15 text-brand-accent border border-brand-accent/30">
                        Latest
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-brand-subtle mt-0.5">v{entry.version} · {fmtDate(entry.date)}</div>
                  <p className="text-sm text-brand-text/85 mt-2 leading-relaxed">{entry.description}</p>
                  {Array.isArray(entry.highlights) && entry.highlights.length > 0 && (
                    <ul
                      className="mt-2.5 space-y-1 text-sm text-brand-text/80"
                      data-testid={`whats-new-highlights-${entry.version}`}
                    >
                      {entry.highlights.map((h, i) => (
                        <li key={i} className="flex gap-2 leading-snug">
                          <span className="text-brand-accent mt-0.5">•</span>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="px-6 py-4 border-t border-brand-border/60">
          <button
            data-testid="whats-new-done"
            onClick={onClose}
            className="td-btn-primary w-full !py-2.5"
          >
            Got it — happy walking
          </button>
        </div>
      </div>
    </div>
  );
}
